import { z } from "zod";
/**
 * Patient-supplied medical records - the imaging reports, pathology and
 * discharge summaries a specialist needs before an opinion means anything.
 *
 * Uploads go straight from the browser to S3 with a presigned URL. The file
 * never passes through Lambda, which keeps a 25 MB scan off a 512 MB function
 * and out of CloudWatch, and means the bytes touch exactly one service.
 */
/**
 * What a patient may upload.
 *
 * An allowlist rather than a blocklist: this bucket's contents are handed back
 * to a clinician's browser, so the question is not "is this dangerous" but "is
 * this something a specialist could actually read". Anything active - HTML,
 * SVG, scripts - is absent on purpose, because a presigned GET renders on our
 * origin's terms.
 */
export const ALLOWED_DOCUMENT_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/tiff",
    // DICOM, for imaging exported straight from a scanner.
    "application/dicom",
    "text/plain",
];
export const documentContentTypeSchema = z.enum(ALLOWED_DOCUMENT_TYPES);
/** 25 MB. Comfortably holds a scanned report; a full DICOM study is out of scope. */
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
/** Ten per consultation is generous for a second opinion and bounds the cost. */
export const MAX_DOCUMENTS_PER_APPOINTMENT = 10;
export const DOCUMENT_KINDS = [
    "imaging_report",
    "pathology",
    "discharge_summary",
    "lab_results",
    "referral_letter",
    "prescription",
    "other",
];
export const documentKindSchema = z.enum(DOCUMENT_KINDS);
export function documentKindLabel(kind) {
    switch (kind) {
        case "imaging_report":
            return "Imaging report";
        case "pathology":
            return "Pathology";
        case "discharge_summary":
            return "Discharge summary";
        case "lab_results":
            return "Lab results";
        case "referral_letter":
            return "Referral letter";
        case "prescription":
            return "Prescription";
        case "other":
            return "Other";
    }
}
/**
 * A filename is displayed to a clinician and forms part of an S3 key, so it is
 * constrained rather than sanitised. Rejecting an odd name is honest; silently
 * rewriting one means the specialist sees a different filename to the one the
 * patient sent, which matters when they are cross-referencing a paper file.
 */
export const documentFilenameSchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9][A-Za-z0-9 ._()-]*$/, "Use letters, numbers, spaces, and . _ ( ) - only, starting with a letter or number.")
    .refine((name) => !name.includes(".."), "Filename may not contain '..'.");
export const requestUploadSchema = z.object({
    filename: documentFilenameSchema,
    content_type: documentContentTypeSchema,
    size_bytes: z.number().int().positive().max(MAX_DOCUMENT_BYTES),
    kind: documentKindSchema.default("other"),
    note: z.string().trim().max(500).optional(),
});
/**
 * Object key for a document.
 *
 * Prefixed by appointment so a future lifecycle rule, or a deletion request,
 * can operate on one consultation without touching another. The document id
 * segment means two files with the same name never collide.
 */
export function documentKey(appointmentId, documentId, filename) {
    return `appointments/${appointmentId}/${documentId}/${filename}`;
}
export function isAllowedContentType(value) {
    return ALLOWED_DOCUMENT_TYPES.includes(value);
}
export function formatBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
//# sourceMappingURL=documents.js.map